"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveLookupDerived = exports.resolveLookupSearchFields = exports.crudLookupItemSchema = exports.crudLookupReplyBody = exports.crudLookupQueryShape = exports.LOOKUP_TEXT_MAX_LENGTH = exports.LOOKUP_DEFAULT_MAX_LIMIT = void 0;
const zod_1 = require("zod");
const id_1 = require("./id");
const error_codes_1 = require("./error-codes");
/**
 * Справочное чтение `q.<entity>.lookup` (эпик ACCESS).
 *
 * Формам нужен список для выпадающего выбора (очереди, операторы, шаблоны), а полный
 * `q.<e>.list` требует права на саму сущность и отдаёт её целиком. `lookup` — узкая
 * проекция: только `id` + явно перечисленные в `defineEntity(..., { lookup })` поля,
 * текстовый поиск и потолок `limit`. Доступен каждому вошедшему человеку тенанта —
 * поэтому в список полей нельзя класть ничего чувствительного (креды, хосты, ПДн).
 */
/** Потолок `limit` по умолчанию: справочник для выпадашки, не выгрузка. */
exports.LOOKUP_DEFAULT_MAX_LIMIT = 500;
/** Потолок длины текстового фильтра. */
exports.LOOKUP_TEXT_MAX_LENGTH = 128;
/**
 * Тело запроса: необязательные текстовый фильтр (подстрока, без учёта регистра, по
 * `searchFields` сущности) и `limit`. Envelope (`QueryMessage`) добавляет вызывающий.
 */
const crudLookupQueryShape = (maxLimit) => ({
    text: zod_1.z.string().max(exports.LOOKUP_TEXT_MAX_LENGTH).optional(),
    limit: zod_1.z.number().int().positive().max(maxLimit).optional(),
});
exports.crudLookupQueryShape = crudLookupQueryShape;
/**
 * Reply: `{isOk:true, items, total}` | `{isOk:false, errorCode}`. `total` — сколько
 * строк подошло под фильтр без учёта `limit` (форма может показать «ещё N»).
 */
const crudLookupReplyBody = (itemSchema) => zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({
        isOk: zod_1.z.literal(true),
        items: zod_1.z.array(itemSchema),
        total: zod_1.z.number().int().nonnegative(),
    }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        errorCode: zod_1.z.enum(error_codes_1.CrudErrorCodes),
        error: zod_1.z.string().optional(),
        details: zod_1.z.record(zod_1.z.unknown()).optional(),
    }),
]);
exports.crudLookupReplyBody = crudLookupReplyBody;
/** Схема элемента: `id` + `fields`, взятые из snapshot-схемы сущности. */
const crudLookupItemSchema = (snapshotShape, fields, entityName) => {
    const shape = { id: id_1.EntityId };
    for (const field of fields) {
        const schema = snapshotShape[field];
        if (!schema) {
            throw new Error(`defineEntity('${entityName}'): lookup.fields содержит '${field}', которого нет в snapshot`);
        }
        shape[field] = schema;
    }
    return zod_1.z.object(shape);
};
exports.crudLookupItemSchema = crudLookupItemSchema;
const unwrapName = (schema) => {
    let current = schema;
    for (;;) {
        const name = current._def.typeName ?? '';
        if (name === 'ZodOptional')
            current = current.unwrap();
        else if (name === 'ZodNullable')
            current = current.unwrap();
        else if (name === 'ZodDefault')
            current = current.removeDefault();
        else
            return name;
    }
};
/**
 * Поля, по которым работает текстовый фильтр: явный `searchFields` либо строковые
 * поля из `fields`. Пустой результат допустим — тогда `text` игнорируется.
 */
const resolveLookupSearchFields = (snapshotShape, fields, searchFields, entityName) => {
    if (searchFields) {
        for (const field of searchFields) {
            if (unwrapName(snapshotShape[field] ?? zod_1.z.never()) !== 'ZodString') {
                throw new Error(`defineEntity('${entityName}'): lookup.searchFields '${field}' не строковое поле snapshot`);
            }
        }
        return searchFields;
    }
    return fields.filter((field) => unwrapName(snapshotShape[field] ?? zod_1.z.never()) === 'ZodString');
};
exports.resolveLookupSearchFields = resolveLookupSearchFields;
/**
 * Проверка `lookup.derived` при определении сущности: источники есть в snapshot,
 * имена производных полей не пересекаются ни с `id`, ни с `fields` — иначе
 * вычисленное значение молча перекрыло бы настоящее поле.
 */
const resolveLookupDerived = (snapshotShape, fields, derived, entityName) => {
    for (const field of derived.from) {
        if (!snapshotShape[field]) {
            throw new Error(`defineEntity('${entityName}'): lookup.derived.from содержит '${field}', которого нет в snapshot`);
        }
    }
    const names = Object.keys(derived.shape);
    for (const derivedName of names) {
        if (derivedName === 'id' || fields.includes(derivedName)) {
            throw new Error(`defineEntity('${entityName}'): lookup.derived '${derivedName}' совпадает с полем справочника`);
        }
    }
    return { from: derived.from, fields: names, compute: derived.compute };
};
exports.resolveLookupDerived = resolveLookupDerived;
//# sourceMappingURL=lookup-zod.js.map